import { alpha, Theme } from "@mui/material/styles";

import { ALPHA } from "./designTokens";

/**
 * Theme utility functions to ensure consistent styling across the application.
 * See .agent/skills/ui-design-system/SKILL.md for design principles.
 */

// ─── Shared style objects (import these instead of repeating inline) ──────────

/**
 * Section label typography — small uppercase label used before every content section.
 * Replaces the 7x-repeated inline sx object across the codebase.
 *
 * Usage:
 *   <Typography variant="subtitle2" sx={sectionLabelSx}>Section Name</Typography>
 */
export const sectionLabelSx = {
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  fontSize: "0.6875rem",
} as const;

/**
 * Table head cell typography — consistent uppercase column headers.
 *
 * Usage:
 *   <TableCell sx={tableHeadCellSx}>Column</TableCell>
 */
export const tableHeadCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  fontSize: "0.875rem",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
} as const;

// ─── Theme-dependent utilities ────────────────────────────────────────────────

/**
 * Bordered table container — wraps <Table> instead of using <Card>.
 * Enforces I-6: Tables in bordered boxes, never in Cards.
 *
 * Usage:
 *   <Box sx={getTableContainerSx(theme)}><Table>...</Table></Box>
 */
export const getTableContainerSx = (theme: Theme) =>
  ({
    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
    borderRadius: 2,
    overflow: "hidden",
  }) as const;

/**
 * Table head row styles — tinted background + column header typography.
 *
 * Usage:
 *   <TableRow sx={getTableHeadRowSx(theme)}>
 */
export const getTableHeadRowSx = (theme: Theme) =>
  ({
    bgcolor: alpha(theme.palette.primary.main, ALPHA.TABLE_HEADER),
    "& th": tableHeadCellSx,
  }) as const;

/**
 * Format cloud provider enum values into display names.
 * Handles both snake_case and uppercase forms.
 *
 * Usage:
 *   <Chip label={formatCloudProvider(system.cloudProvider)} />
 */
export function formatCloudProvider(cp: string | undefined | null): string {
  if (!cp) return "—";
  const map: Record<string, string> = {
    rusha_hosted: "Rusha Hosted",
    aws_eks: "AWS EKS",
    gcp_gke: "GCP GKE",
    azure_aks: "Azure AKS",
    k8s_self_hosted: "Self-Hosted K8s",
    aws_services: "AWS Services",
    gcp_services: "GCP Services",
    azure_services: "Azure Services",
  };
  return map[cp.toLowerCase()] ?? cp;
}

/**
 * Consistent system/resource status chip props.
 * Enforces a single status → color mapping across the entire application.
 *
 * Usage:
 *   const chipProps = getStatusChipProps(resource.status);
 *   <Chip {...chipProps} size="small" sx={{ fontWeight: 500 }} />
 */
export function getStatusChipProps(status: string): {
  label: string;
  color: "success" | "error" | "warning" | "info" | "default";
  variant: "filled" | "outlined";
} {
  const s = (status ?? "").toLowerCase();
  /** Convert snake_case to Title Case: "provisioning_failed" → "Provisioning Failed" */
  const toTitleCase = (str: string) =>
    str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  if (s === "running" || s === "active" || s === "healthy")
    return { label: toTitleCase(s), color: "success", variant: "filled" };
  if (s === "vcs_provisioned")
    return { label: "Ready", color: "success", variant: "outlined" };
  if (s === "failed" || s === "error" || s === "unhealthy")
    return { label: toTitleCase(s), color: "error", variant: "outlined" };
  if (s === "building")
    return { label: "Building", color: "info", variant: "outlined" };
  if (s === "deploying")
    return { label: "Deploying", color: "warning", variant: "outlined" };
  if (s === "provisioning")
    return { label: "Provisioning", color: "default", variant: "outlined" };
  if (s === "provisioning_failed")
    return {
      label: "Provisioning Failed",
      color: "error",
      variant: "outlined",
    };
  if (s === "stopped")
    return { label: "Stopped", color: "default", variant: "outlined" };
  if (s === "draft")
    return { label: "Draft", color: "warning", variant: "outlined" };
  if (s === "deleted")
    return { label: "Deleted", color: "error", variant: "outlined" };
  if (s === "pending" || s === "warming" || s === "degraded" || s === "paused")
    return { label: toTitleCase(s), color: "warning", variant: "outlined" };
  return {
    label: toTitleCase(s) || status,
    color: "default",
    variant: "outlined",
  };
}

/**
 * Standard empty state container.
 * Enforces I-7: dashed border box with consistent spacing.
 *
 * Usage:
 *   <Box sx={emptyStateContainerSx(theme)}>...</Box>
 */
export const getEmptyStateSx = (theme: Theme) =>
  ({
    py: 6,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 1.5,
    border: `1px dashed ${alpha(theme.palette.divider, 0.12)}`,
    borderRadius: 2,
  }) as const;

/**
 * Card styles with configurable parameters
 */
export const getCardStyles = (
  theme: Theme,
  options?: {
    hasShadow?: boolean;
    hasBorder?: boolean;
    isInteractive?: boolean;
    isGlass?: boolean;
  },
) => {
  const {
    hasShadow = true,
    hasBorder = true,
    isInteractive = false,
    isGlass = false,
  } = options || {};

  return {
    borderRadius: 2,
    bgcolor: isGlass
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: isGlass ? "blur(16px) saturate(180%)" : undefined,
    boxShadow: hasShadow
      ? `0 ${isInteractive ? "4px" : "2px"} 8px ${alpha(theme.palette.common.black, 0.05)}`
      : "none",
    border: hasBorder
      ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
      : "none",
    p: 2,
    transition: "box-shadow 150ms ease, border-color 150ms ease",
    ...(isInteractive
      ? {
          "&:hover": {
            borderColor: alpha(theme.palette.divider, 0.12),
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          },
        }
      : {}),
  } as const;
};

/**
 * Button styles with consistent theme application
 */
export const getButtonStyles = (
  theme: Theme,
  variant: "contained" | "outlined" | "text" = "contained",
) => {
  const baseStyles = {
    borderRadius: theme.shape.borderRadius,
    textTransform: "none" as const,
    fontWeight: 600,
    transition: "all 150ms ease-out",
  };

  switch (variant) {
    case "contained":
      return {
        ...baseStyles,
        background: theme.palette.primary.main,
        boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        "&:hover": {
          background: theme.palette.primary.dark,
          boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
        },
      };
    case "outlined":
      return {
        ...baseStyles,
        borderWidth: "1.5px",
        borderColor: alpha(theme.palette.primary.main, 0.5),
        "&:hover": {
          borderWidth: "1.5px",
          borderColor: theme.palette.primary.main,
          background: alpha(theme.palette.primary.main, 0.05),
        },
      };
    case "text":
      return {
        ...baseStyles,
        "&:hover": {
          background: alpha(theme.palette.primary.main, 0.05),
        },
      };
  }
};

/**
 * Input field styles for consistent form elements
 */
export const getInputStyles = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  "& .MuiInputBase-input": {
    background: "none",
  },
});

/**
 * Focus ring styles for form inputs (TextField, Select).
 * Uses the shared FOCUS_RING token for consistent keyboard focus across all forms.
 *
 * Usage:
 *   <TextField sx={getFocusRingSx(theme)} />
 *   <Select sx={getFocusRingSx(theme)} />
 */
export const getFocusRingSx = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    transition: "all 150ms ease-out",
    "& fieldset": {
      borderColor: alpha(theme.palette.text.primary, 0.12),
    },
    "&:hover fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
});

/**
 * Link styles with animation effects
 */
export const getLinkStyles = (theme: Theme) => ({
  textDecoration: "none",
  fontWeight: 500,
  color: theme.palette.primary.main,
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    width: "0%",
    height: "2px",
    bottom: -2,
    left: 0,
    background: theme.palette.primary.main,
    transition: "width 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover": {
    color: theme.palette.primary.dark,
    textDecoration: "none",
    "&:after": {
      width: "100%",
    },
  },
});

/**
 * Header/title styles for consistent typography
 */
export const getTitleStyles = (
  theme: Theme,
  variant: "page" | "section" | "subsection",
  options?: { heroGradient?: boolean },
) => {
  switch (variant) {
    case "page":
      if (options?.heroGradient) {
        return {
          fontSize: "1.75rem",
          fontWeight: 700,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          mb: 2,
        };
      }
      return {
        fontSize: "1.75rem",
        fontWeight: 700,
        color: theme.palette.text.primary,
        mb: 2,
      };
    case "section":
      return {
        fontSize: "1.25rem",
        fontWeight: 600,
        mb: 1.5,
        color: theme.palette.text.primary,
      };
    case "subsection":
      return {
        fontSize: "1rem",
        fontWeight: 600,
        mb: 1,
        color: theme.palette.text.secondary,
      };
  }
};

/**
 * Container styles for page layouts
 */
export const getContainerStyles = (theme: Theme, maxWidth = "xl") => ({
  maxWidth: maxWidth,
  px: { xs: 2, sm: 3, md: 4 },
  py: 3,
});

/**
 * Progress indicator styles
 */
export const getProgressStyles = (theme: Theme) => ({
  height: 6,
  borderRadius: 3,
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  ".MuiLinearProgress-bar": {
    borderRadius: 3,
  },
});

/**
 * Glass panel effect for modern UI components
 */
export const getGlassPanelStyles = (theme: Theme) => ({
  backdropFilter: "blur(16px) saturate(180%)",
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  boxShadow: `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)}, 0 2px 4px -1px ${alpha(theme.palette.common.black, 0.03)}`,
  borderRadius: theme.shape.borderRadius,
});
